import DetailVerticalList from "@imminently/decisively-ui/DetailVerticalList";
import DetailHorizontalList from "@imminently/decisively-ui/DetailHorizontalList";
import DetailTable from "@imminently/decisively-ui/DetailTable";
import DetailGrid from "@imminently/decisively-ui/DetailGrid";
import Icon from "@imminently/decisively-ui/Icon";
import * as React from "react";
import { ComponentPage, ComponentPreview, ComponentSection } from "../components/ComponentPage";

export function DetailPage() {
  const [gridColumns, setGridColumns] = React.useState<"responsive" | 1 | 2 | 3>(2);

  return (
    <ComponentPage
      title="Detail Components"
      description="Showcase for Detail, DetailTable, DetailHorizontalList, and DetailVerticalList."
    >
      <ComponentSection
        title="DetailTable"
        description="Tabular detail rows with icon, title, and content."
      >
        <ComponentPreview className="items-stretch justify-stretch">
          <div className="w-full max-w-2xl space-y-4">
            <div className="rounded-md border border-border p-4">
              <DetailTable>
                <DetailTable.Item>
                  <DetailTable.Icon><Icon name="value" /></DetailTable.Icon>
                  <DetailTable.Title>Status</DetailTable.Title>
                  <DetailTable.Content>Ready</DetailTable.Content>
                </DetailTable.Item>
                <DetailTable.Item>
                  <DetailTable.Icon><Icon name="date" /></DetailTable.Icon>
                  <DetailTable.Title>Updated</DetailTable.Title>
                  <DetailTable.Content>Today</DetailTable.Content>
                </DetailTable.Item>
                <DetailTable.Item>
                  <DetailTable.Icon><Icon name="entity" /></DetailTable.Icon>
                  <DetailTable.Title>Owner</DetailTable.Title>
                  <DetailTable.Content>Jane Doe</DetailTable.Content>
                </DetailTable.Item>
              </DetailTable>
            </div>

            <div className="rounded-md border border-border p-4">
              <DetailTable>
                <DetailTable.Item>
                  <DetailTable.Title>Status</DetailTable.Title>
                  <DetailTable.Content>Ready</DetailTable.Content>
                </DetailTable.Item>
                <DetailTable.Item>
                  <DetailTable.Title>Updated</DetailTable.Title>
                  <DetailTable.Content>Today</DetailTable.Content>
                </DetailTable.Item>
                <DetailTable.Item>
                  <DetailTable.Title>Owner</DetailTable.Title>
                  <DetailTable.Content>Jane Doe</DetailTable.Content>
                </DetailTable.Item>
              </DetailTable>
            </div>
          </div>
        </ComponentPreview>
      </ComponentSection>

      <ComponentSection
        title="DetailGrid"
        description="Grid layout for key/value metadata using the same detail item subcomponents and container queries."
      >
        <ComponentPreview className="items-stretch justify-stretch">
          <div className="w-full max-w-4xl rounded-md border border-border p-4">
            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <label htmlFor="detail-grid-columns" className="text-sm font-medium text-foreground">Columns</label>
                <select
                  id="detail-grid-columns"
                  value={String(gridColumns)}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value === "responsive") setGridColumns("responsive");
                    else setGridColumns(Number(value) as 1 | 2 | 3);
                  }}
                  className="h-9 rounded-md border border-border bg-background px-2 text-sm"
                >
                  <option value="responsive">Responsive</option>
                  <option value="1">1</option>
                  <option value="2">2</option>
                  <option value="3">3</option>
                </select>
              </div>
              <div className="max-w-full overflow-x-auto rounded-md border border-border p-3">
                  <DetailGrid columns={gridColumns}>
                    <DetailGrid.Item><DetailGrid.Icon><Icon name="value" /></DetailGrid.Icon><DetailGrid.Title>Event</DetailGrid.Title><DetailGrid.Content>Session Started</DetailGrid.Content></DetailGrid.Item>
                    <DetailGrid.Item><DetailGrid.Icon><Icon name="value" /></DetailGrid.Icon><DetailGrid.Title>Status</DetailGrid.Title><DetailGrid.Content>Success</DetailGrid.Content></DetailGrid.Item>
                    <DetailGrid.Item><DetailGrid.Icon><Icon name="entity" /></DetailGrid.Icon><DetailGrid.Title>Request ID</DetailGrid.Title><DetailGrid.Content>req_12345</DetailGrid.Content></DetailGrid.Item>
                    <DetailGrid.Item><DetailGrid.Icon><Icon name="entity" /></DetailGrid.Icon><DetailGrid.Title>Path</DetailGrid.Title><DetailGrid.Content>/decisionapi/session/model/release</DetailGrid.Content></DetailGrid.Item>
                    <DetailGrid.Item><DetailGrid.Icon><Icon name="entity" /></DetailGrid.Icon><DetailGrid.Title>User</DetailGrid.Title><DetailGrid.Content>Jane Doe</DetailGrid.Content></DetailGrid.Item>
                    <DetailGrid.Item><DetailGrid.Icon><Icon name="date" /></DetailGrid.Icon><DetailGrid.Title>Region</DetailGrid.Title><DetailGrid.Content>APAC</DetailGrid.Content></DetailGrid.Item>
                  </DetailGrid>
              </div>
              <div className="max-w-full overflow-x-auto rounded-md border border-border p-3">
                  <DetailGrid columns={gridColumns}>
                    <DetailGrid.Item><DetailGrid.Title>Event</DetailGrid.Title><DetailGrid.Content>Session Started</DetailGrid.Content></DetailGrid.Item>
                    <DetailGrid.Item><DetailGrid.Title>Status</DetailGrid.Title><DetailGrid.Content>Success</DetailGrid.Content></DetailGrid.Item>
                    <DetailGrid.Item><DetailGrid.Title>Request ID</DetailGrid.Title><DetailGrid.Content>req_12345</DetailGrid.Content></DetailGrid.Item>
                    <DetailGrid.Item><DetailGrid.Title>Path</DetailGrid.Title><DetailGrid.Content>/decisionapi/session/model/release</DetailGrid.Content></DetailGrid.Item>
                    <DetailGrid.Item><DetailGrid.Title>User</DetailGrid.Title><DetailGrid.Content>Jane Doe</DetailGrid.Content></DetailGrid.Item>
                    <DetailGrid.Item><DetailGrid.Title>Region</DetailGrid.Title><DetailGrid.Content>APAC</DetailGrid.Content></DetailGrid.Item>
                  </DetailGrid>
              </div>
            </div>
          </div>
        </ComponentPreview>
      </ComponentSection>

      <ComponentSection
        title="DetailHorizontalList"
        description="Horizontal layout with one or more banner items."
      >
        <ComponentPreview className="items-stretch justify-stretch">
          <div className="w-full max-w-2xl rounded-md border border-border p-4">
            <DetailHorizontalList>
              <DetailHorizontalList.Item>
                <DetailHorizontalList.Icon><Icon name="entity" /></DetailHorizontalList.Icon>
                <DetailHorizontalList.Title>Environment</DetailHorizontalList.Title>
                <DetailHorizontalList.Content>Production</DetailHorizontalList.Content>
              </DetailHorizontalList.Item>
              <DetailHorizontalList.Item>
                <DetailHorizontalList.Icon><Icon name="date" /></DetailHorizontalList.Icon>
                <DetailHorizontalList.Title>Updated</DetailHorizontalList.Title>
                <DetailHorizontalList.Content>Today</DetailHorizontalList.Content>
              </DetailHorizontalList.Item>
              <DetailHorizontalList.Item>
                <DetailHorizontalList.Icon><Icon name="value" /></DetailHorizontalList.Icon>
                <DetailHorizontalList.Title>Score</DetailHorizontalList.Title>
                <DetailHorizontalList.Content>94%</DetailHorizontalList.Content>
              </DetailHorizontalList.Item>
            </DetailHorizontalList>
          </div>
        </ComponentPreview>
      </ComponentSection>

      <ComponentSection
        title="DetailVerticalList"
        description="Vertical list composed of multiple DetailVerticalList.Item entries."
      >
        <ComponentPreview className="items-stretch justify-stretch">
          <div className="w-full max-w-2xl rounded-md border border-border p-4 space-y-4">
            <DetailVerticalList>
              <DetailVerticalList.Item>
                <DetailVerticalList.Icon><Icon name="error" /></DetailVerticalList.Icon>
                <DetailVerticalList.Title>Priority</DetailVerticalList.Title>
                <DetailVerticalList.Content>High</DetailVerticalList.Content>
              </DetailVerticalList.Item>
              <DetailVerticalList.Item>
                <DetailVerticalList.Icon><Icon name="entity" /></DetailVerticalList.Icon>
                <DetailVerticalList.Title>Scope</DetailVerticalList.Title>
                <DetailVerticalList.Content>Global</DetailVerticalList.Content>
              </DetailVerticalList.Item>
              <DetailVerticalList.Item>
                <DetailVerticalList.Icon><Icon name="date" /></DetailVerticalList.Icon>
                <DetailVerticalList.Title>Release</DetailVerticalList.Title>
                <DetailVerticalList.Content>2026.05</DetailVerticalList.Content>
              </DetailVerticalList.Item>
            </DetailVerticalList>

            <DetailVerticalList>
              <DetailVerticalList.Item>
                <DetailVerticalList.Title>Owner</DetailVerticalList.Title>
                <DetailVerticalList.Content>Jane Doe</DetailVerticalList.Content>
              </DetailVerticalList.Item>
              <DetailVerticalList.Item>
                <DetailVerticalList.Title>Region</DetailVerticalList.Title>
                <DetailVerticalList.Content>APAC</DetailVerticalList.Content>
              </DetailVerticalList.Item>
              <DetailVerticalList.Item>
                <DetailVerticalList.Title>Notes</DetailVerticalList.Title>
                <DetailVerticalList.Content>No icon variant for compact metadata sections.</DetailVerticalList.Content>
              </DetailVerticalList.Item>
            </DetailVerticalList>
          </div>
        </ComponentPreview>
      </ComponentSection>
    </ComponentPage>
  );
}
